import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useResizeDetector } from 'react-resize-detector';
import clsx from 'clsx';
import s from './Shadow.module.css';

function Shadow({ minWidth = 500, children, ...otherProps }) {
  const [shadowStart, setShadowStart] = useState(false);
  const [shadowEnd, setShadowEnd] = useState(false);
  const [currentMinWidth, setCurrentMinWidth] = useState(minWidth);
  const wrapperRef = useRef(null);

  const updateShadowHeight = useCallback(() => {
    if (wrapperRef.current) {
      const shadowElements = wrapperRef.current.querySelectorAll(`.${s.shadow}`);
      Array.prototype.forEach.call(shadowElements, (el) => {
        /* eslint no-param-reassign: ["error", { "props": false }] */
        el.style.height = `${wrapperRef.current.clientHeight}px`;
      });
    }
  }, []);

  const handleScroll = useCallback((e) => {
    const scrolled = Math.ceil(e.target.scrollLeft);
    const scrollable = e.target.scrollWidth - e.target.offsetWidth;

    if (scrolled > 0 && scrolled < scrollable) {
      setShadowEnd(true);
      setShadowStart(true);
    } else if (scrolled === 0 && currentMinWidth >= e.target.offsetWidth) {
      setShadowStart(false);
      setShadowEnd(true);
    } else if (scrolled === scrollable && currentMinWidth >= e.target.offsetWidth) {
      setShadowStart(true);
      setShadowEnd(false);
    }
  }, [currentMinWidth]);

  const onResize = useCallback((width) => {
    if (currentMinWidth > width) {
      setShadowEnd(true);
    } else {
      setShadowStart(false);
      setShadowEnd(false);
    }
    updateShadowHeight();
  }, [currentMinWidth, updateShadowHeight]);

  const { ref: resizeRef } = useResizeDetector({
    handleWidth: true,
    handleHeight: false,
    onResize: ({ width }) => onResize(width),
  });

  // Update current min width when prop changes
  useEffect(() => {
    setCurrentMinWidth(minWidth);
  }, [minWidth]);

  // Add scroll listener and update shadow height on mount and updates
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (wrapper) {
      wrapper.addEventListener('scroll', handleScroll, { passive: true });
      updateShadowHeight();

      return () => {
        wrapper.removeEventListener('scroll', handleScroll);
      };
    }
  }, [handleScroll, updateShadowHeight]);

  // Combine refs
  const setRefs = useCallback((node) => {
    wrapperRef.current = node;
    resizeRef(node);
  }, [resizeRef]);

  const containerClasses = clsx('ng-ui-shadow', s.shadowContainer);
  const shadowRightClasses = clsx(s.shadow, shadowEnd && s.shadowRight);
  const shadowLeftClasses = clsx(s.shadow, shadowStart && s.shadowLeft);

  return (
    <div
      className={containerClasses}
      ref={setRefs}
      {...otherProps}
    >
      <div className={shadowRightClasses} />
      <div className={shadowLeftClasses} />
      {children}
    </div>
  );
}

Shadow.propTypes = {
  minWidth: PropTypes.number,
  children: PropTypes.node.isRequired,
};

export default Shadow;
